import React, { useState, useEffect, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { LocalizationProvider } from './contexts/LocalizationContext'
import { AuthForm } from './components/Auth/AuthForm'
import { Dashboard } from './components/Dashboard/Dashboard'
import { SubscriptionGuard } from './components/Subscription/SubscriptionGuard'
import { Landing } from './pages/Landing'
import Terms from './pages/Terms'
import Privacy from './pages/Privacy'
import LegalNotice from './pages/LegalNotice'
import Contact from './pages/Contact'
import AnalyticsService from './services/analyticsService'

// Page d'abonnement live (daily/PAYG/monthly, PayZen + fallback Stripe)
const SubDirectZxsemCrypto = React.lazy(() => import('./pages/SubDirectZxsemCrypto'))

function SubscriptionEntryPoint() {
  const [searchParams] = useSearchParams()
  const queryMode = searchParams.get('sub') || searchParams.get('mode')
  const persistedMode = sessionStorage.getItem('preferred_subscription_mode') || localStorage.getItem('preferred_subscription_mode')
  const subscriptionMode = queryMode || persistedMode

  // Bascule : la route de prod /subscription rend désormais la version avec
  // l'option de paiement CRYPTO (−50%). Le flux existant (daily/PAYG/Stripe/
  // SystemPay) est conservé tel quel dans cette copie ; on ajoute juste la carte.
  if (subscriptionMode === 'direct' || subscriptionMode === 'direct2') {
    return <SubDirectZxsemCrypto />
  }

  return <SubDirectZxsemCrypto />
}

// Composant pour tracker les changements de page
function PageTracker() {
  const location = useLocation()

  useEffect(() => {
    // Tracker chaque changement de page
    AnalyticsService.trackPageView(location.pathname, document.title)

    // Persister le mode d'abonnement lorsqu'il est présent dans l'URL
    const params = new URLSearchParams(location.search)
    const modeFromUrl = params.get('sub') || params.get('mode')
    if (modeFromUrl === 'direct' || modeFromUrl === 'direct2') {
      sessionStorage.setItem('preferred_subscription_mode', modeFromUrl)
      localStorage.setItem('preferred_subscription_mode', modeFromUrl)
    }
  }, [location])

  return null
}

// Component for protected routes that require authentication
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="min-h-screen bg-paybyphone-light flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-paybyphone-green mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/auth" replace state={{ from: location.pathname + location.search }} />
  }

  return <>{children}</>
}

// Auth page component
function AuthPage() {
  const { user } = useAuth()
  const location = useLocation() as any
  const [searchParams] = useSearchParams()
  const authModeParam = searchParams.get('auth') || searchParams.get('mode')
  const initialMode = authModeParam === 'signup' ? 'signup' : 'signin'
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>(initialMode)

  // Propager le paramètre `payment` vers /subscription pour garantir le bon provider.
  // Exemple: /auth?mode=signup&payment=stripe -> /subscription?payment=stripe
  const paymentParamRaw = searchParams.get('payment')
  const paymentParam = paymentParamRaw === 'stripe' || paymentParamRaw === 'systempay' ? paymentParamRaw : null
  const modeParam = searchParams.get('sub') || searchParams.get('mode')
  const sessionMode = sessionStorage.getItem('preferred_subscription_mode') || localStorage.getItem('preferred_subscription_mode')
  const directModeFromSession = sessionMode === 'direct' || sessionMode === 'direct2'
  const directModeParam =
    modeParam === 'direct' || modeParam === 'direct2'
      ? modeParam
      : directModeFromSession
        ? sessionMode
        : null

  const stateRedirect = location?.state?.from as string | undefined
  // Si on force un mode/provider depuis la campagne, on priorise toujours /subscription
  // (sinon un state.from='/dashboard' peut faire perdre mode=direct)
  const forceSubscriptionRedirect = directModeParam === 'direct' || !!paymentParam
  const redirectTo = directModeParam === 'direct2'
    ? '/dashboard'
    : forceSubscriptionRedirect
      ? '/subscription'
      : (stateRedirect || '/dashboard')

  const resolvedRedirectTo = React.useMemo(() => {
    // On ne force que pour les pages de paiement/abonnement
    const isSubscriptionTarget = redirectTo.startsWith('/subscription')

    if (!isSubscriptionTarget || (!paymentParam && !directModeParam)) return redirectTo

    const [path, queryString = ''] = redirectTo.split('?')
    const params = new URLSearchParams(queryString)

    if (paymentParam && !params.has('payment')) {
      params.set('payment', paymentParam)
    }

    if (directModeParam && !params.has('sub')) {
      params.set('sub', directModeParam)
    }

    const nextQuery = params.toString()
    return nextQuery ? `${path}?${nextQuery}` : path
  }, [redirectTo, paymentParam, directModeParam])

  // If user is already logged in, redirect to the page they originally requested
  if (user) {
    return <Navigate to={resolvedRedirectTo} replace />
  }

  return (
    <AuthForm
      mode={authMode}
      onToggleMode={() => setAuthMode(authMode === 'signin' ? 'signup' : 'signin')}
    />
  )
}

function App() {
  useEffect(() => {
    // Initialiser Google Analytics au démarrage de l'application
    AnalyticsService.init()
  }, [])

  return (
    <LocalizationProvider>
      <AuthProvider>
        <Router>
          <PageTracker />
          <Suspense fallback={
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          }>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Landing />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/legal" element={<LegalNotice />} />
              <Route path="/contact" element={<Contact />} />
              
              {/* Protected routes */}
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <SubscriptionGuard>
                      <Dashboard />
                    </SubscriptionGuard>
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/subscription"
                element={
                  <ProtectedRoute>
                    <SubscriptionEntryPoint />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscription-crypto"
                element={
                  <ProtectedRoute>
                    <SubDirectZxsemCrypto />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Suspense>
        </Router>
      </AuthProvider>
    </LocalizationProvider>
  )
}

export default App
